import { state, style, trigger } from '@angular/animations';
import { AsyncPipe, isPlatformServer } from '@angular/common';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostBinding,
  inject,
  makeStateKey,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  RESPONSE_INIT,
  signal,
  TransferState,
  WritableSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { Banner, LineMap, ProductAdditions, ProductDetails, ProductModule, ProductTypes, Saving } from 'src/app/models';
import { BYPASS_INTERCEPTOR } from 'src/app/models/error';
import { SharedDetailContent } from 'src/app/models/product-detail';
import { AuthService } from 'src/app/services/auth';
import { BannerService } from 'src/app/services/banner.service';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { ContentService } from 'src/app/services/content/content.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { CenterSectionComponent } from 'src/app/shared/components';
import { BreadcrumbComponent } from 'src/app/shared/components/breadcrumb/breadcrumb.component';
import { HeroBannerComponent } from 'src/app/shared/components/hero-banner/hero-banner.component';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { environment } from 'src/environments/environment';
import { GtmService } from '../../../services/analytics/gtm.service';
import { CompositeProductDetailComponent } from './nxt/composite/composite-product-detail.component';
import { SimpleProductDetailComponent } from './nxt/simple/simple-product-detail.component';
import { OnlineProductDetailComponent } from './online/online-product-detail.component';

const PRODUCT_DATA_KEY = makeStateKey<ProductDetails>('productDetailsData');

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  imports: [
    BreadcrumbComponent,
    MatIconModule,
    SimpleProductDetailComponent,
    CompositeProductDetailComponent,
    OnlineProductDetailComponent,
    CenterSectionComponent,
    HeroBannerComponent,
    AsyncPipe,
  ],
})
export class ProductDetailComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private basketQuery = inject(BasketQuery);
  private authService = inject(AuthService);
  private gtmService = inject(GtmService);
  private themeService = inject(ThemeService);
  private kioskService = inject(KioskService);
  private bannerService = inject(BannerService);
  protected sanitizer = inject(DomSanitizer);
  private elRef = inject(ElementRef);
  private response = inject(RESPONSE_INIT, { optional: true });
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);

  private readonly productEndpoint = `${environment.apiOrigin}/Product`;
  private readonly productMemberEndpoint = `${environment.apiOrigin}/Product/member`;
  readonly cmsOrigin = environment.cmsOrigin;

  private requestEndpoint: string;
  private subscription = new Subscription();

  @HostBinding('style.background-image') backgroundStyle: SafeStyle;
  @HostBinding('class.kiosk') isKiosk: boolean;

  productData: ProductDetails | null = null;

  product: WritableSignal<ProductDetails> = signal(null);
  productTypes = ProductTypes;
  customerId: number;
  sharedDetailContent: SharedDetailContent;
  price: number;
  savings: Saving;
  basketProducts$: Observable<LineMap>;
  productBanners$: Observable<Banner[]>;

  productNotFound: boolean;
  productNotFoundWarning: string;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'productInfo');

    const sharedContent = contentService.getSharedContent();
    this.sharedDetailContent = {
      addToBasketLabel: sharedContent.get('addToBasketText'),
      maxQuantityTitle: sharedContent.get('maxQuantityTitleText'),
      maxQuantityPriceExplaination: sharedContent.get('maxQuantityExplainationTemplate'),
      productNotForSale: this.content.get('productNotForSaleText') || 'Udsolgt',
      contactUs: this.content.get('contactUsText') || 'Kontakt os',
    };

    this.productNotFoundWarning = this.content.get('productNotFoundText');
    this.isKiosk = this.kioskService.isKiosk;

    effect(() => {
      const product = this.product();
      if (product === null) return;

      this.backgroundStyle = '';
      if (product.backgroundImageUrl) {
        let imageStyle = `url(${this.cmsOrigin}${product.backgroundImageUrl})`;
        if (product.backgroundImageDarken) imageStyle = `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), ${imageStyle}`;
        this.backgroundStyle = this.sanitizer.bypassSecurityTrustStyle(imageStyle);
      }

      this.price = product.price;
      if (product.splash && product.originalPrice !== product.price) {
        const sharedContent = this.contentService.getSharedContent();

        const savingLabel = sharedContent.get('savingsLabelText');
        const savingPrice = product.originalPrice - product.price;

        this.savings = {
          quantity: 1,
          label: savingLabel,
          price: savingPrice,
        };
      }

      this.gtmService?.viewItem(product);

      const merchant = this.contentService.getMerchant();
      this.productBanners$ = this.bannerService.getHeroBanners(merchant.id, product.id);

      product.productDisplayTitle = product.productDisplayTitle?.replaceAll('{butiksnavn}', merchant.name) || '';
      product.productPageTitle = product.productPageTitle?.replaceAll('{butiksnavn}', merchant.name) || '';
      product.productPageDescription = product.productPageDescription?.replaceAll('{butiksnavn}', merchant.name) || '';
      this.setSEO(product);
    });
  }

  ngOnInit() {
    this.basketProducts$ = this.basketQuery.selectProducts$;
    this.requestEndpoint = this.productEndpoint;

    if (this.transferState.hasKey(PRODUCT_DATA_KEY)) {
      this.product.set(this.transferState.get(PRODUCT_DATA_KEY, null));
      this.transferState.remove(PRODUCT_DATA_KEY);
    } else {
      this.productNotFound = false;
    }

    this.subscription.add(
      this.authService.member.subscribe((member) => {
        const id = this.parseId(this.activatedRoute.snapshot.url[0].path);

        this.requestEndpoint = member ? this.productMemberEndpoint : this.productEndpoint;
        this.customerId = member ? this.authService.getCustomerDetails(member).id : null;
        this.getProduct(id);
      }),
    );

    this.subscription.add(
      this.router.events.subscribe((val) => {
        if (val instanceof NavigationEnd) {
          const id = this.parseId(this.activatedRoute.snapshot.url[0].path);
          if (this.product().id === id) return;

          this.productNotFound = false;
          this.getProduct(id);
        }
      }),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.contentService.setOGImage();
    this.subscription.unsubscribe();
  }

  private getProduct(id: number) {
    let url = `${this.requestEndpoint}/${id}?merchantId=${this.contentService.getMerchantId()}`;
    if (this.customerId) {
      url += `&customerId=${this.customerId}`;
    }

    this.http
      .get<ProductDetails>(url, { context: new HttpContext().set(BYPASS_INTERCEPTOR, true) })
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return of(null);
        }),
        take(1),
      )
      .subscribe({
        next: async (product: ProductDetails) => {
          if (!product) return;

          let frontendDisplay = false;
          for (const category of product.categories) {
            frontendDisplay = category.frontPageCategory || category.frontendDisplay || category.isInherited;
            if (frontendDisplay) break;
          }

          if (!frontendDisplay || product.hidden) {
            this.productNotFound = true;
            this.cdr.markForCheck();
            return;
          }

          product = await this.getProductDetails(product);
          this.product.set(product);

          if (isPlatformServer(this.platformId)) {
            this.transferState.set(PRODUCT_DATA_KEY, product);
          }

          this.cdr.markForCheck();
        },
      });
  }

  private async getProductDetails(product: ProductDetails): Promise<ProductDetails> {
    let moduleUrl = `${this.requestEndpoint}/${product.id}/Modules?merchantId=${this.contentService.getMerchantId()}`;
    if (this.customerId) {
      moduleUrl += `&customerId=${this.customerId}`;
    }

    const modules = await firstValueFrom(this.http.get<ProductModule[]>(moduleUrl));
    product.productModules = modules?.length ? modules : [];

    const additionsUrl = `${this.productEndpoint}/${product.id}/Additions?merchantId=${this.contentService.getMerchantId()}`;
    const additions = await firstValueFrom(this.http.get<ProductAdditions[]>(additionsUrl));
    product.productAdditions = this.filterAdditions(additions);

    return product;
  }

  private filterAdditions(additions: ProductAdditions[]): ProductAdditions[] {
    if (!additions?.length) return [];
    for (const addition of additions) {
      addition.products = addition.products.filter((product) => !product.hidden);
    }
    return additions.filter((addition) => addition.products.length > 0);
  }

  private parseId(path: string): number {
    const {
      groups: { complex, simple },
    } = /^.*-(?<complex>\d+)$|(?<simple>\d+)$/.exec(path);
    const id = complex || simple;

    return +id;
  }

  private handleError(error: HttpErrorResponse) {
    this.productNotFound = true;
    this.response.status = 404;
    this.cdr.markForCheck();
  }

  private setSEO(product: ProductDetails) {
    const { productPageTitle, productPageDescription, productDisplayName, primaryImageUrl } = product;

    this.contentService.setTitle(productPageTitle ? productPageTitle : productDisplayName);
    this.contentService.setDescription(productPageDescription);
    this.contentService.setNoIndex(false);
    this.contentService.setOGImage(primaryImageUrl);
  }
}
